/* @jsx h */
import { defineComponent, h, inject } from "vue";
import { Reservation } from "@lemmonhq/types";
import { useLocalization } from "@lemmonhq/uikit";
import { formatDateRelative, formatTimeInTz } from "@lemmonhq/orders";
import {
  categorizeReservation,
  getArrivalState,
  RESERVATION_STATUS_STYLE,
  ARRIVAL_STYLE,
  TONE_CLASSES,
  type StatusTone,
} from "../../../utils/reservationStatus";

export interface ReservationsListProps {
  reservations: Reservation[];
  nowMs: number;
  onEnterDropMode?: (reservation: Reservation) => void;
}

export const ReservationsList = defineComponent<ReservationsListProps>({
  name: "ReservationsList",
  props: ["reservations", "nowMs", "onEnterDropMode"],
  setup(props) {
    const { t } = useLocalization();
    const timezone = inject<{ value: string }>("timezone", { value: "UTC" });

    return () => (
      <div class="space-y-1.5">
        {props.reservations.map((reservation) => {
          const hasAssignedTables = reservation.table_assignments?.length > 0;

          // Badge reflects STATUS, not table presence. A pending *request*
          // (Pending tab) reads "Pending"; a real reservation reads "Confirmed"
          // even when it has no table yet (it's still confirmed); seated parties
          // read "Seated".
          const status = reservation.status as string;
          const isPendingRequest = status === "pending";
          const category = categorizeReservation(status);

          let statusLabel: string;
          let tone: StatusTone;
          if (isPendingRequest) {
            statusLabel = t("reservations.table_popup.pending_tab");
            tone = "warning";
          } else {
            const style = RESERVATION_STATUS_STYLE[category];
            statusLabel = t(style.labelKey);
            tone = style.tone;
          }

          // Arrival urgency only applies to upcoming (confirmed/changed)
          // reservations — not requests, and not parties already seated (those
          // always read "Seated"). One shared 15-min-soon / 30-min-late window.
          const arrival =
            !isPendingRequest && category === "upcoming"
              ? getArrivalState(reservation.time, props.nowMs)
              : "upcoming";
          if (arrival !== "upcoming") {
            const a = ARRIVAL_STYLE[arrival];
            statusLabel = t(a.labelKey);
            tone = a.tone;
          }
          const { badgeBg: statusBg, badgeText: statusText } =
            TONE_CLASSES[tone];

          return (
            <div
              key={reservation.id}
              class="bg-base-100 rounded-lg shadow-sm border border-base-300/30 hover:border-primary/40 hover:shadow-md transition-all overflow-hidden cursor-pointer group"
              onClick={() => props.onEnterDropMode?.(reservation)}
            >
              <div class="px-3 py-2.5">
                {/* Main row: Time | Status | Name | Guests+Duration */}
                <div class="flex items-center gap-2.5">
                  {/* Time */}
                  <div class="flex-shrink-0" style="width: 56px;">
                    <div class="text-sm font-bold text-accent leading-tight">
                      {formatTimeInTz(reservation.time, timezone.value)}
                    </div>
                    <div class="text-[10px] text-base-content/50 font-medium uppercase tracking-wide">
                      {formatDateRelative(reservation.time, timezone.value, {
                        today: t("general.date.today"),
                        tomorrow: t("general.date.tomorrow"),
                      })}
                    </div>
                  </div>

                  {/* Status badge */}
                  <div class="flex-shrink-0" style="width: 68px;">
                    <div
                      class={`flex items-center justify-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold ${statusBg} ${statusText}`}
                      style={
                        arrival === "late"
                          ? "animation: pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;"
                          : arrival === "soon"
                            ? "animation: pulse 3s cubic-bezier(0.4, 0, 0.6, 1) infinite;"
                            : ""
                      }
                    >
                      <span class="truncate">{statusLabel}</span>
                    </div>
                  </div>

                  {/* Name + guests/duration */}
                  <div class="min-w-0 flex-1">
                    <div class="text-sm font-semibold text-accent truncate">
                      {reservation.party_name ||
                        t("reservations.general.guest_label")}
                    </div>
                    <div class="flex items-center gap-2 mt-0.5">
                      <span class="flex items-center gap-0.5 text-xs text-base-content/60">
                        <span class="material-symbols-outlined text-xs">
                          group
                        </span>
                        {reservation.party_size}
                      </span>
                      <span class="flex items-center gap-0.5 text-xs text-base-content/45">
                        <span class="material-symbols-outlined text-xs">
                          schedule
                        </span>
                        {reservation.duration_mins}m
                      </span>
                      {reservation.notes && (
                        <span
                          class="material-symbols-outlined text-xs text-teal-400"
                          title={reservation.notes}
                        >
                          sticky_note_2
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Table badges — if assigned */}
                  {hasAssignedTables && (
                    <div class="flex-shrink-0 flex gap-0.5">
                      {reservation.table_assignments.length <= 2 ? (
                        reservation.table_assignments.map((a) => (
                          <span
                            key={a.id}
                            class="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-500/10 text-purple-400 border border-purple-500/20"
                          >
                            {a.table_name}
                          </span>
                        ))
                      ) : (
                        <span class="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-500/10 text-purple-400 border border-purple-500/20">
                          <span class="material-symbols-outlined text-xs">
                            table_restaurant
                          </span>
                          {reservation.table_assignments.length}
                        </span>
                      )}
                    </div>
                  )}

                  {/* No table indicator */}
                  {!hasAssignedTables && (
                    <div class="flex-shrink-0">
                      <span class="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-base-content/5 text-base-content/30 border border-base-content/10">
                        <span class="material-symbols-outlined text-xs">
                          table_restaurant
                        </span>
                        No table
                      </span>
                    </div>
                  )}

                  {/* Chevron */}
                  <div class="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                    <span class="material-symbols-outlined text-base text-base-content/25">
                      chevron_right
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {props.reservations.length === 0 && (
          <div class="text-center  py-6 text-sm">
            {t("reservations.table_popup.no_reservations_category")}
          </div>
        )}
      </div>
    );
  },
});
